import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import Cookies from "universal-cookie";

/**
 * Custom hook to check granular permissions for the currently selected company.
 */
export const usePermissions = (overrideCompanyId = null) => {
  const cookies = new Cookies();
  const context = useOutletContext() || {};
  const currentCompany = overrideCompanyId || context.currentCompany || cookies.get("defaultcompany");
  const user = useSelector((state) => state.mainReducer || {});

  // Robust Role Detection
  const isGlobalSuperAdmin = !!cookies.get("defaulttenant") || String(cookies.get("isGlobalSuperAdmin")) === "true";
  const isTenantAdmin = String(cookies.get("isTenantAdmin")) === "true" || cookies.get("isTenantAdmin") === 1 || cookies.get("isTenantAdmin") === "1";
  const isSystemAdmin = String(cookies.get("isSystemAdmin")) === "true" || cookies.get("isSystemAdmin") === 1 || cookies.get("isSystemAdmin") === "1";

  /**
   * Maps old granular permission checks to the new 7 simplified permissions.
   * Old: "Users: Read", "Users: Create", "Companies: Update", etc.
   * New: "Edit Users", "Edit Company", "Edit Categories", "Edit Metadata", "Reporting", "Scan", "Search"
   */
  const resolvePermission = (permission) => {
    const p = permission.toLowerCase().trim();

    // User/Users → Edit Users
    if (p.startsWith("user")) return "edit users";
    // Company/Companies → Edit Company
    if (p.startsWith("compan")) return "edit company";
    // Criteria/Metadata → Edit Metadata
    if (p.startsWith("criteria") || p.includes("metadata")) return "edit metadata";
    // Category/Categories → Edit Categories
    if (p.startsWith("categor")) return "edit categories";
    // Security Profiles → Edit Users (folded in)
    if (p.startsWith("security")) return "edit users";
    // Reporting/Audit → Reporting
    if (p.startsWith("reporting") || p.startsWith("audit")) return "reporting";
    // Invoices/Billing → Reporting (folded in)
    if (p.startsWith("invoice") || p.startsWith("billing")) return "reporting";
    // Search
    if (p.includes("search")) return "search";
    // Scan
    if (p.includes("scan")) return "scan";

    // Fallback: return as-is for direct matches (e.g., "Edit Users")
    return p;
  };

  /**
   * Checks if the user has a specific permission in the current company context.
   * @param {string} permission - The permission string (e.g., "Users: Read", "Edit Users", "Search")
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    const pClean = permission.toLowerCase().trim();

    // 0. EXPLICIT RESTRICTION: Global Admins cannot create companies
    if (pClean === "companies: create" && (isGlobalSuperAdmin || cookies.get("defaulttenant"))) {
      return false;
    }

    // 1. Global Super Admin/Tenant Admin bypass (Master Key) for EVERYTHING ELSE
    if (isGlobalSuperAdmin || isTenantAdmin || cookies.get("defaulttenant")) return true;

    // 2. Allow read, create, and delete access for CRUD modules without permission checks. Only update/edit requires a check!
    if (
      pClean.includes("read") ||
      pClean.includes("create") ||
      pClean.includes("delete")
    ) {
      if (!pClean.includes("search") && !pClean.includes("scan") && !pClean.includes("reporting")) {
        return true;
      }
    }

    // 3. Resolve the requested permission to our simplified 7-permission model
    const resolved = resolvePermission(permission);

    // 3. Check the user's levelList for the current company
    const companyAccess = user?.accessList?.find(
      (acc) => Number(acc.companyID) === Number(currentCompany)
    );

    if (companyAccess && companyAccess.levelList && companyAccess.levelList.length > 0) {
      return companyAccess.levelList.some(l => resolvePermission(l) === resolved);
    }

    return false;
  };

  /**
   * Checks if user has access to a specific category in the current company.
   * @param {string} categoryName 
   * @returns {boolean}
   */
  const hasCategoryAccess = (categoryName) => {
    const companyAccess = user?.accessList?.find(
      (acc) => Number(acc.companyID) === Number(currentCompany)
    );

    if (companyAccess && companyAccess.categoryList) {
      if (companyAccess.categoryList.includes(categoryName)) return true;
    }

    if (isGlobalSuperAdmin || isTenantAdmin || isSystemAdmin) return true;
    return false;
  };

  // ✅ Hydration check: We are "hydrating" if we have a token but no user data in Redux
  // We check userId instead of tenantID because Global Super Admins don't have a tenantID.
  const isHydrating = !!cookies.get("token") && !user.userId && !user.userID;

  return {
    hasPermission,
    hasCategoryAccess,
    isGlobalSuperAdmin,
    isTenantAdmin,
    isSystemAdmin,
    currentCompany,
    isLoading: isHydrating,
    resolvePermission
  };
};
